// Tiny helper: given a hex accent colour, derive `--acc-soft` (12% tint on
// white) and `--acc-ink` (a slightly darker shade for hover states) so the
// entire instacut.css `--acc*` cascade behaves correctly without asking
// admins to pick three colours.

const HEX_RE = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

const normalise = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const m = hex.trim().match(HEX_RE);
  if (!m) return null;
  let v = m[1];
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  return `#${v.toLowerCase()}`;
};

const toRgb = (hex) => {
  const h = normalise(hex);
  if (!h) return null;
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
};

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

const darken = (hex, amount = 0.16) => {
  const rgb = toRgb(hex);
  if (!rgb) return hex;
  const r = clamp(rgb.r * (1 - amount));
  const g = clamp(rgb.g * (1 - amount));
  const b = clamp(rgb.b * (1 - amount));
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

const softTint = (hex, alpha = 0.14) => {
  const rgb = toRgb(hex);
  if (!rgb) return '#eceaff';
  const w = 255;
  const r = clamp(w * (1 - alpha) + rgb.r * alpha);
  const g = clamp(w * (1 - alpha) + rgb.g * alpha);
  const b = clamp(w * (1 - alpha) + rgb.b * alpha);
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
};

export const deriveAccentTokens = (hex) => {
  const acc = normalise(hex);
  if (!acc) return null;
  return {
    acc,
    accSoft: softTint(acc, 0.14),
    accInk: darken(acc, 0.16),
  };
};

// Apply/reset accent CSS variables on a root element (default: <html>).
export const applyAccentVars = (hex, root = document.documentElement) => {
  const tokens = deriveAccentTokens(hex);
  if (!tokens) {
    root.style.removeProperty('--acc');
    root.style.removeProperty('--acc-soft');
    root.style.removeProperty('--acc-ink');
    return;
  }
  root.style.setProperty('--acc', tokens.acc);
  root.style.setProperty('--acc-soft', tokens.accSoft);
  root.style.setProperty('--acc-ink', tokens.accInk);
};

export const clearAccentVars = (root = document.documentElement) => {
  root.style.removeProperty('--acc');
  root.style.removeProperty('--acc-soft');
  root.style.removeProperty('--acc-ink');
};
